import { PaymentTypes } from '@kaltura-ott/ms-ott-clients';
import {
  KalturaBillingItemsType,
  KalturaBillingTransaction,
  KalturaBillingTransactionListResponse,
  KalturaFilterPager,
  KalturaSearchAssetFilter,
  TransactionHistoryListAction,
  KalturaAssetListResponse,
  KalturaAsset,
  KalturaTransactionHistoryFilter,
} from '@kaltura-ott/ott-client-types/api/types';
import { KalturaTransactionType } from '@kaltura-ott/ott-client-types/api/types/KalturaTransactionType';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { KuxMediaAssetType } from '../interfaces';

import { DEFAULT_CHUNK_SIZE, getAssetList } from './asset';
import { assetToType } from './asset-v2.helper';
import { getPageNumberArr } from './asset.helper';
import { kalturaClient } from './kaltura-clent';
import { paymentClientPost } from './payment-client';

export type TransactionsRentArgs = {
  mediaFileId: number;
  price: number;
  currency: string;
  ppvModuleId: string;
  partnerId: number;
  userType: string;
  accessToken: string;
  householdExternalId: string;
  paymentMethodId?: number;
};

export type TransactionsSubscriptionArgs = {
  subscriptionId: number;
  currency: string;
  price: number;
  partnerId: number;
  userType: string;
  accessToken: string;
  householdExternalId: string;
  subscriptionType: string;
  paymentMethodId?: number;
  subscriptionExternalId: string;
};

/**
/**
 * Build request payload for service 'Transaction' action 'Purchase'.
 *
 * Usage: purchase product
 *
 * @category Purchase / Subscriptions
 * @function
 */
export type TransactionsPurchaseArgs = TransactionsRentArgs | TransactionsSubscriptionArgs;

export const transactionsPurchase = (purchaseParams: TransactionsPurchaseArgs) => {
  const contentAndProduct =
    'subscriptionId' in purchaseParams
      ? {
          contentId: Number(purchaseParams.subscriptionId),
          productId: Number(purchaseParams.subscriptionId),
          productType: KalturaTransactionType.subscription,
          subscriptionType: purchaseParams.subscriptionType,
          subscriptionExternalId: purchaseParams.subscriptionExternalId,
        }
      : {
          productId: Number(purchaseParams.ppvModuleId),
          contentId: purchaseParams.mediaFileId,
          productType: KalturaTransactionType.ppv,
        };

  const paymentInfo: PaymentTypes.PurchaseInfo = {
    ...contentAndProduct,
    coupon: '',
    currency: purchaseParams.currency,
    price: purchaseParams.price,
    accessToken: purchaseParams.accessToken,
    userType: purchaseParams.userType,
    householdExternalId: purchaseParams.householdExternalId,
    paymentMethodId: purchaseParams.paymentMethodId,
  };

  return paymentClientPost(purchaseParams.partnerId, { purchase: paymentInfo });
};

/**
 * Get transaction history
 */
export const transactionsHistory = (
  filter: KalturaTransactionHistoryFilter,
  pager: KalturaFilterPager,
): Observable<{ objects: KalturaBillingTransaction[]; totalCount: number }> => {
  const client = kalturaClient();
  const action = new TransactionHistoryListAction({ filter, pager });

  return client.request(action).pipe(
    mergeMap((response: KalturaBillingTransactionListResponse) => {
      const transactionList = response.objects;
      const ppvAssetIds = transactionList
        .filter((item) => item.itemType === KalturaBillingItemsType.ppv)
        .map((item) => item.purchasedItemCode);
      if (!ppvAssetIds.length) {
        return of(response);
      }
      return getAssetList({
        filter: new KalturaSearchAssetFilter({
          kSql: `(and media_id:'${ppvAssetIds.join(',')}')`,
        }),
      }).pipe(
        map((responsePpvAssets: KalturaAssetListResponse) => {
          const adultAssetIds = responsePpvAssets.objects
            .map((item) => assetToType<KalturaAsset, KuxMediaAssetType>(item))
            .filter((item) => !!item?.adult && !!item?.id)
            .map((item) => item?.id);
          return {
            totalCount: response.totalCount,
            objects: transactionList.filter(
              (transaction) => !adultAssetIds.includes(Number(transaction.purchasedItemCode)),
            ),
          };
        }),
      );
    }),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Get transaction history recursively
 */
export const transactionsHistoryRecursively = (): Observable<{
  objects: KalturaBillingTransaction[];
  totalCount: number;
}> => {
  const filter = new KalturaTransactionHistoryFilter({});
  const pager = new KalturaFilterPager({
    pageIndex: 1,
    pageSize: DEFAULT_CHUNK_SIZE,
  });

  return transactionsHistory(filter, pager).pipe(
    switchMap((assets) => {
      const paginatorRequestArr = getPageNumberArr(assets.totalCount, DEFAULT_CHUNK_SIZE, 2).map((pageNumber) => {
        return transactionsHistory(
          filter,
          new KalturaFilterPager({
            pageIndex: pageNumber,
            pageSize: DEFAULT_CHUNK_SIZE,
          }),
        );
      });
      return forkJoin([of(assets), ...paginatorRequestArr]);
    }),
    map((result) => {
      return {
        totalCount: result[0].totalCount,
        objects: result.reduce((prev: KalturaBillingTransaction[], cur) => {
          return [...prev, ...cur.objects];
        }, []),
      };
    }),
  );
};
